import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';

const CONTACT_LABEL = 'Contact';

interface ProfessionalContactTabButtonProps {
    /** The tab's styling, so it matches the links either sub-nav renders. */
    className: string;
}

/**
 * The Contact tab for a professional with no visible location: there is no
 * location page to link to, so the tab opens the enquiry modal instead. Mirrors
 * the legacy desktop header and mobile nav, which keep Contact in the bar for
 * these professionals.
 *
 * Renders nothing when enquiry CTAs are hidden for the current user — the tab
 * would have nowhere to go.
 */
export const ProfessionalContactTabButton = ({
    className,
}: ProfessionalContactTabButtonProps) => {
    const professional = useProfessionalInitialRouteData();
    const { showEnquiryCTA } = useEnquiryVisibility(professional);
    const { openEnquiryModal } = useEnquiryModal(
        {
            professional,
            itemType: 'professional',
            siteTreeID: professional.ID,
            itemTitle: professional.Title,
            includeBranch: true,
            source: 'ProfessionalContactTab',
        },
        // The header CTAs already own the `enquire` URL param — a second
        // auto-opening instance would race them with its own context.
        { autoOpenFromUrl: false },
    );

    if (!showEnquiryCTA) return null;

    return (
        <button
            type="button"
            className={className}
            onClick={() =>
                openEnquiryModal({
                    title: CONTACT_LABEL,
                    ctaLabel: CONTACT_LABEL,
                })
            }
        >
            {CONTACT_LABEL}
        </button>
    );
};
